import type { ReactElement } from 'react';

import { RiFlaskLine } from '@remixicon/react';

import {
  ExperimentConfirmation,
  type ExperimentTreatment,
  type PreviewField,
  type RawExperimentMetric,
  type ToolConfirmationRenderProps,
  getNestedValue,
  isPlainObject,
  parseExperimentMetric,
  resolveInputBody,
} from '@spotify/plugin-confidence-chat-react';
import { formatWeight } from '@spotify/plugin-confidence-workflows-react';

const DEFAULT_PREVIEW_FIELDS: PreviewField[] = [
  { key: 'displayName', label: 'Name' },
  { key: 'abtest.flag', label: 'Flag' },
  { key: 'surfaces', label: 'Surfaces' },
  { key: 'metrics.entity', label: 'Entity' },
  { key: 'metrics.assignmentTable', label: 'Assignment table' },
];

export const CreateAbtestConfirmation = (
  props: ToolConfirmationRenderProps,
): ReactElement => {
  const parsedInput = isPlainObject(props.input)
    ? resolveInputBody(props.input)
    : {};
  const treatments = (getNestedValue(parsedInput, 'abtest.treatments') ??
    []) as ExperimentTreatment[];
  const rawMetrics = (getNestedValue(parsedInput, 'metrics.metrics') ??
    []) as RawExperimentMetric[];
  const metrics = rawMetrics.map(parseExperimentMetric);
  const metricResourceNames = metrics
    .map(metric => metric.metric)
    .filter(Boolean);

  return (
    <ExperimentConfirmation.Root
      {...props}
      icon={<RiFlaskLine size={16} />}
      resourceHints={metricResourceNames}
      title="Create A/B test"
    >
      <ExperimentConfirmation.Config
        input={parsedInput}
        previewFields={DEFAULT_PREVIEW_FIELDS}
      />
      {treatments.length > 0 && (
        <ExperimentConfirmation.Treatments>
          {treatments.map((treatment, index) => (
            <ExperimentConfirmation.Treatment
              key={treatment.variant ?? treatment.name ?? index}
              index={index}
              treatment={treatment}
              weight={
                <ExperimentConfirmation.TreatmentWeight
                  weight={formatWeight(treatment.weight)}
                />
              }
            />
          ))}
        </ExperimentConfirmation.Treatments>
      )}
      {metrics.length > 0 && (
        <ExperimentConfirmation.Metrics metrics={metrics} />
      )}
    </ExperimentConfirmation.Root>
  );
};
